// components/BrokerMessageThread.jsx
//
// Broker-side view of the broker <-> introducer chat thread for a lead
// (`lead_id` = introducer_leads.id — unchanged by conversion, so the same
// component/endpoint works whether `leadId` comes from a Lead row or a
// Deal's `lead_id`). Extracted 6 Aug 2026 from its previous single home in
// pages/LeadsPage.jsx (unchanged logic/markup) so pages/DealDetails.js can
// reuse it for the new "Introducer Conversation" card, instead of a second
// copy drifting out of sync with the first.
import React, { useState, useEffect, useCallback, useRef } from "react";

const tok = () => localStorage.getItem("token");

export default function BrokerMessageThread({ leadId, onUnreadCleared }) {
  const [messages, setMessages] = useState(null);
  const [newMsg, setNewMsg]     = useState("");
  const [sending, setSending]   = useState(false);
  // Real bug found + fixed 6 Aug 2026: scrollIntoView() on an element walks
  // up EVERY scrollable ancestor to bring it into view, not just its
  // immediate parent -- inside the small modal on the Leads page that was
  // invisible, but on a full-length page like DealDetails.js it dragged the
  // whole page down to the bottom of the thread on every load, and users
  // couldn't scroll back up. Scroll only this component's own message list
  // instead, via scrollTop on its container -- never touches page scroll.
  const listRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/introducer-leads/${leadId}/messages`, { headers: { Authorization: `Bearer ${tok()}` } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMessages(data.messages || []);
      if (onUnreadCleared) onUnreadCleared(leadId);
    } catch { setMessages([]); }
  }, [leadId, onUnreadCleared]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await fetch(`/api/introducer-leads/${leadId}/messages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tok()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message: newMsg.trim() }),
      });
      setNewMsg(""); load();
    } finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
      ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }) + " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };

  if (messages === null) return <div style={{ color: "#a0aec0", fontSize: 12, padding: "8px 0" }}>Loading messages…</div>;

  return (
    <div>
      <div ref={listRef} style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, padding: "12px 0 8px", marginBottom: 10 }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: "center", padding: "16px 0", color: "#94a3b8", fontSize: 13, fontStyle: "italic" }}>
            No messages yet — start the conversation below
          </div>
        ) : messages.map(m => {
          const isMe = m.sender_role === "broker";
          const initials = isMe ? "Me" : (m.sender_name || "Int").slice(0, 2).toUpperCase();
          return (
            <div key={m.id} style={{ display: "flex", alignItems: "flex-end", gap: 8, flexDirection: isMe ? "row-reverse" : "row" }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                background: isMe ? "#0e1b2c" : "#dbeafe",
                color: isMe ? "#10b981" : "#1e40af",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, letterSpacing: "0.02em",
              }}>{initials}</div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: isMe ? "flex-end" : "flex-start", maxWidth: "72%" }}>
                <div style={{
                  padding: "9px 13px", fontSize: 13, lineHeight: 1.5, wordBreak: "break-word",
                  background: isMe ? "#0e1b2c" : "#eff6ff",
                  color: isMe ? "#e2e8f0" : "#1e40af",
                  borderRadius: isMe ? "14px 14px 3px 14px" : "14px 14px 14px 3px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                }}>{m.message}</div>
                <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{fmtTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message… (Enter to send)"
          style={{ flex: 1, padding: "9px 13px", borderRadius: 20, fontSize: 13, outline: "none", background: "#fff", border: "1.5px solid #e2e8f0", color: "#111827" }}
        />
        <button onClick={send} disabled={sending || !newMsg.trim()}
          style={{
            background: sending || !newMsg.trim() ? "#e2e8f0" : "#10b981",
            color: sending || !newMsg.trim() ? "#94a3b8" : "#0e1b2c",
            border: "none", borderRadius: 20, padding: "9px 18px", fontWeight: 700, fontSize: 13,
            cursor: sending || !newMsg.trim() ? "not-allowed" : "pointer", whiteSpace: "nowrap",
          }}>{sending ? "…" : "↑ Send"}</button>
      </div>
    </div>
  );
}
